import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getConfiguration, getSocialLinks } from '../redux/actions/action-creator'
import { footerCustomerSupportLinks, footerInformationLinks } from './footerNavigation'
import './footer.css'

const fallbackSocialLinks = []

const socialMeta = [
    {
        key: 'chat',
        label: 'Chat With Us',
        iconClassName: 'ph-fill ph-chat-circle-dots',
        color: '#00B300',
        keywords: ['chatwithus', 'chat', 'tawk'],
    },
    {
        key: 'facebook',
        label: 'Facebook',
        iconClassName: 'ph-fill ph-facebook-logo',
        color: '#1877F2',
        keywords: ['facebook', 'fb'],
    },
    {
        key: 'instagram',
        label: 'Instagram',
        iconClassName: 'ph-fill ph-instagram-logo',
        color: '#E4405F',
        keywords: ['instagram', 'insta'],
    },
    {
        key: 'twitter',
        label: 'Twitter',
        iconClassName: 'ph-fill ph-twitter-logo',
        color: '#1DA1F2',
        keywords: ['twitter', 'xcom', 'tco'],
    },
    {
        key: 'linkedin',
        label: 'LinkedIn',
        iconClassName: 'ph-fill ph-linkedin-logo',
        color: '#0A66C2',
        keywords: ['linkedin'],
    },
    {
        key: 'youtube',
        label: 'YouTube',
        iconClassName: 'ph-fill ph-youtube-logo',
        color: '#FF0000',
        keywords: ['youtube', 'youtu'],
    },
    {
        key: 'pinterest',
        label: 'Pinterest',
        iconClassName: 'ph-fill ph-pinterest-logo',
        color: '#E60023',
        keywords: ['pinterest'],
    },
    {
        key: 'whatsapp',
        label: 'WhatsApp',
        iconClassName: 'ph-fill ph-whatsapp-logo',
        color: '#25D366',
        keywords: ['whatsapp', 'wa'],
    },
    {
        key: 'telegram',
        label: 'Telegram',
        iconClassName: 'ph-fill ph-telegram-logo',
        color: '#26A5E4',
        keywords: ['telegram', 'tme'],
    },
    {
        key: 'tiktok',
        label: 'TikTok',
        iconClassName: 'ph-fill ph-tiktok-logo',
        color: '#000000',
        keywords: ['tiktok'],
    },
    {
        key: 'web',
        label: 'Web',
        iconClassName: 'ph-fill ph-globe',
        color: '#3A418A',
        keywords: ['web', 'website', 'shoprghtnow', 'rghtnow'],
    },
]

const unknownSocialMeta = {
    key: 'website',
    label: 'Website',
    iconClassName: 'ph-fill ph-globe',
    color: '#3A418A',
}

const socialLinkKeys = [
    'redirect',
    'redirect_link',
    'redirectLink',
    'social_link',
    'socialLink',
    'social_url',
    'socialUrl',
    'url',
    'link',
    'href',
]

const stripMarkdownLink = (url) => {
    const trimmedUrl = String(url || '').trim()
    const markdownLink = trimmedUrl.match(/\[[^\]]*]\(<?([^)>]+)>?\)/)

    return (markdownLink ? markdownLink[1] : trimmedUrl).trim()
}

const socialNameKeys = [
    'name',
    'title',
    'platform',
    'type',
    'social_name',
    'socialName',
    'social_media',
    'socialMedia',
    'social_media_name',
    'socialMediaName',
]

const getCustomerValue = (customer, keys, fallback) => {
    const value = keys.find((key) => customer?.[key] !== null && customer?.[key] !== undefined && customer?.[key] !== '')

    return value ? customer[value] : fallback
}

const getSocialLinksArray = (payload) => {
    const candidates = [
        payload?.data?.social_links,
        payload?.data?.data?.social_links,
        payload?.social_links,
        payload?.response_data?.social_links,
        payload?.data?.response_data?.social_links,
        payload?.data,
        payload,
    ]

    return candidates.find((item) => Array.isArray(item)) || []
}

const getFirstValue = (item, keys) => {
    const key = keys.find((field) => item?.[field] !== null && item?.[field] !== undefined && item?.[field] !== '')

    return key ? item[key] : ''
}

const getSocialUrl = (item) => {
    if (typeof item === 'string') return item

    return getFirstValue(item, socialLinkKeys)
}

const normalizeSocialText = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '')

const getSocialName = (item) => {
    if (typeof item === 'string') return ''

    return getFirstValue(item, socialNameKeys)
}

const getSocialMeta = (item) => {
    const socialName = getSocialName(item)
    const sourceText = normalizeSocialText(`${socialName} ${getSocialUrl(item)} ${item?.icon || ''}`)

    return socialMeta.find((meta) =>
        meta.keywords.some((keyword) => sourceText.includes(normalizeSocialText(keyword)))
    ) || unknownSocialMeta
}

const normalizeSocialUrl = (url) => {
    const trimmedUrl = stripMarkdownLink(url)
        .replace(/\\([&?=])/g, '$1')
        .replace(/&amp;/g, '&')
        .trim()

    if (!trimmedUrl || trimmedUrl === '#') return ''
    if (/^(https?:|mailto:|tel:)/i.test(trimmedUrl)) return trimmedUrl
    if (trimmedUrl.startsWith('//')) return `https:${trimmedUrl}`
    if (trimmedUrl.startsWith('www.')) return `https://${trimmedUrl}`

    return `https://${trimmedUrl.replace(/^\/+/, '')}`
}

const isGenericSocialHomepage = (url) => {
    const normalizedUrl = String(url || '').toLowerCase().replace(/\/+$/, '')

    return [
        'https://www.facebook.com',
        'https://facebook.com',
        'https://www.instagram.com',
        'https://instagram.com',
        'https://www.linkedin.com',
        'https://linkedin.com',
        'https://www.twitter.com',
        'https://twitter.com',
        'https://www.x.com',
        'https://x.com',
    ].includes(normalizedUrl)
}

const normalizeSocialLinks = (links) =>
    links
        .map((item, index) => {
            const meta = getSocialMeta(item)
            const url = normalizeSocialUrl(getSocialUrl(item))

            if (!url || isGenericSocialHomepage(url)) return null

            return {
                id: item?.id || item?.social_link_id || `${meta.key}-${index}`,
                label: getSocialName(item) || meta.label,
                url,
                iconClassName: meta.iconClassName,
                color: meta.color,
            }
        })
        .filter(Boolean)

const Footer = () => {
    const dispatch = useDispatch()
    const configurationData = useSelector((state) => state.common?.configurationData || null)
    const socialLinksData = useSelector((state) => state.common?.socialLinksData || [])
    const [apiSocialLinks, setApiSocialLinks] = useState([])

    useEffect(() => {
        if (!configurationData) {
            dispatch(getConfiguration()).catch(() => {})
        }
    }, [configurationData, dispatch])

    useEffect(() => {
        let mounted = true

        dispatch(getSocialLinks())
            .then((response) => {
                if (mounted) {
                    setApiSocialLinks(getSocialLinksArray(response))
                }
            })
            .catch(() => {
                if (mounted) {
                    setApiSocialLinks([])
                }
            })

        return () => {
            mounted = false
        }
    }, [dispatch])

    const footerSocialLinks = useMemo(() => {
        const apiLinks = normalizeSocialLinks(apiSocialLinks.length ? apiSocialLinks : socialLinksData)

        return apiLinks.length ? apiLinks : normalizeSocialLinks(fallbackSocialLinks)
    }, [apiSocialLinks, socialLinksData])

    const customer = configurationData?.customer_support || configurationData?.customer || configurationData?.data?.customer_support || configurationData?.data?.customer || {}
    const customerAddress = getCustomerValue(customer, ['company_address1', 'company_address', 'companyAddress', 'address'], 'RGHT NOW LLC-FZ, Dubai, United Arab Emirates')
    // const customerPhone = getCustomerValue(customer, ['phone', 'mobile', 'mobile_number', 'mobileNumber', 'company_phone', 'companyPhone'], '+00 971 504 781 269')
    // const customerEmail = getCustomerValue(customer, ['mail', 'email', 'company_email', 'companyEmail'], 'admin@rghtnow.com')

    return (
        <footer className="footer py-24 d-none d-lg-block">
            <img
                src="assets/images/bg/body-bottom-bg.png"
                alt="RGHT NOW footer background"
                className="body-bottom-bg"
            />
            <div className="container container-lg">
                <div className="footer-item-wrapper rghtnow-footer-main d-flex align-items-start flex-wrap">
                    <div className="footer-item">
                        <div className="footer-item__logo">
                            <Link to="/">
                                {" "}
                                <img src={`${process.env.PUBLIC_URL}/assets/images/logo/rghtnow-blue-logo.png`} alt="RGHT NOW" />
                            </Link>
                        </div>
                        <p className="mb-24">
                            RGHT NOW connects customers with trusted local stores, services, and delivery partners.
                        </p>
                        <div className="flex-align gap-16 mb-16">
                            <span className="w-32 h-32 flex-center rounded-circle rghtnow-footer-contact-icon text-white text-md flex-shrink-0">
                                <i className="ph-fill ph-map-pin" />
                            </span>
                            <span className="text-md text-gray-600">
                                {customerAddress}
                            </span>
                        </div>
                        {/* <div className="flex-align gap-16 mb-16">
                            <span className="w-32 h-32 flex-center rounded-circle rghtnow-footer-contact-icon text-white text-md flex-shrink-0">
                                <i className="ph-fill ph-phone-call" />
                            </span>
                            <div className="flex-align gap-16 flex-wrap">
                                <Link
                                    to={`tel:${customerPhone}`}
                                    className="text-md text-gray-600"
                                >
                                    {customerPhone}
                                </Link>
                            </div>
                        </div> */}
                        {/* <div className="flex-align gap-16 mb-16">
                            <span className="w-32 h-32 flex-center rounded-circle rghtnow-footer-contact-icon text-white text-md flex-shrink-0">
                                <i className="ph-fill ph-envelope" />
                            </span>
                            <Link
                                to={`mailto:${customerEmail}`}
                                className="text-md text-gray-600"
                            >
                                {customerEmail}
                            </Link>
                        </div> */}
                    </div>
                    <div className="footer-item">
                        <h6 className="footer-item__title">Information</h6>
                        <ul className="footer-menu">
                            {footerInformationLinks.map((item, index) => (
                                <li className={index === footerInformationLinks.length - 1 ? "" : "mb-16"} key={item.to}>
                                    <Link to={item.to} className="text-gray-600">
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="footer-item">
                        <h6 className="footer-item__title">Customer Support</h6>
                        <ul className="footer-menu">
                            {footerCustomerSupportLinks.map((item, index) => (
                                <li className={index === footerCustomerSupportLinks.length - 1 ? "" : "mb-16"} key={item.to}>
                                    <Link to={item.to} className="text-gray-600">
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="footer-item">
                        <h6 className="">Shop on The Go</h6>
                        <p className="mb-16">RGHT NOW app is available. Get it now</p>
                        <div className="rghtnow-footer-apps my-32">
                            <a href="https://apps.apple.com/us/app/rght-now/id6446073858" target="_blank" rel="noreferrer" aria-label="Download RGHT NOW on the App Store" className="rghtnow-footer-app-badge">
                                <img src={`${process.env.PUBLIC_URL}/assets/images/app-store/app-store-badge.webp`} width="1024" height="304" alt="Download on the App Store" />
                            </a>
                            <a href="https://play.google.com/store/apps/details?id=com.rghtnow.user" target="_blank" rel="noreferrer" aria-label="Download RGHT NOW on Google Play" className="rghtnow-footer-app-badge">
                                <img src={`${process.env.PUBLIC_URL}/assets/images/app-store/google-play-badge.png`} width="1024" height="303" alt="Get it on Google Play" />
                            </a>
                        </div>
                        {footerSocialLinks.length > 0 && (
                            <ul className="flex-align gap-16 rghtnow-footer-social-list">
                                {footerSocialLinks.map((item) => (
                                    <li key={item.id}>
                                        <a
                                            href={item.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label={item.label}
                                            title={item.label}
                                            className="w-44 h-44 flex-center text-xl rounded-circle rghtnow-footer-social-link"
                                            style={{
                                                '--rghtnow-footer-social-color': item.color,
                                                '--rghtnow-footer-social-background': item.color,
                                            }}
                                        >
                                            <i className={item.iconClassName} />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </footer>

    )
}

export default Footer
